"use client";

import Link from "next/link";
import { Role } from "@prisma/client";
import { cn } from "@/lib/utils";
import AuthStatusButton from "./AuthStatusButton";
import AuctionsTab from "./NavBarAuctionsTab";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "../ui/navigation-menu";
import { ExtendedSession } from "@/lib/interface/ExtendedSession";
import { navButtonAdmin, navButtonDefault } from "./styles";
import { Accordion } from "../ui/accordion";

type Props = {
  session: ExtendedSession | null;
  orientation?: "horizontal" | "vertical";
};

export default function NavLinks({ session, orientation = "horizontal" }: Props) {
  const navButtonStyle = session?.role === Role.ADMIN ? navButtonAdmin : navButtonDefault;
  const isVertical = orientation === "vertical";
  const menuClassName = isVertical ? "w-full max-w-none justify-start" : undefined;

  if (!session) {
    if (isVertical) {
      return (
        <div className="w-full max-w-none flex flex-col items-stretch space-y-2">
          <Link href={"/"} className={cn(navigationMenuTriggerStyle(), navButtonStyle, "w-full px-4")}>Home</Link>
          <Accordion type="multiple" className="w-full">
            <AuctionsTab navButtonStyle={navButtonStyle} variant="accordion" />
          </Accordion>
          <Link href={"/api/auth/signin"} className={cn(navigationMenuTriggerStyle(), navButtonStyle, "w-full px-4")}>Log in</Link>
        </div>
      );
    }
    return (
      <NavigationMenu className={menuClassName}>
        <NavigationMenuList>
          <NavigationMenuItem className={cn(navigationMenuTriggerStyle(), navButtonStyle)}>
            <NavigationMenuLink href={"/"}>Home</NavigationMenuLink>
          </NavigationMenuItem>
          <AuctionsTab navButtonStyle={navButtonStyle} />
          <NavigationMenuItem className={cn(navigationMenuTriggerStyle(), navButtonStyle)}>
            <NavigationMenuLink href={"/api/auth/signin"}>Log in</NavigationMenuLink>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
    );
  }

  const links = session.role === Role.BUYER
    ? [
        { label: "Home", href: "/" },
        { label: "Auctions", href: "/", component: () => <AuctionsTab navButtonStyle={navButtonStyle} variant={isVertical ? "accordion" : "dropdown"} /> },
        { label: "Account", href: "/account/details", component: () => (
            <AuthStatusButton session={session} navButtonStyle={navButtonStyle} variant={isVertical ? "accordion" : "dropdown"} />
          ) },
      ]
    : [
        { label: "Home", href: "/" },
        // { label: "Auctions", href: "/admin/auctions" },
        { label: "Account", href: "/account/details", component: () => (
            <AuthStatusButton session={session} navButtonStyle={navButtonStyle} variant={isVertical ? "accordion" : "dropdown"} />
          ) },
      ];

  if (isVertical) {
    const simpleLinks = links.filter((l) => !l.component);
    const accordionItems = links.filter((l) => !!l.component);
    return (
      <div className="w-full max-w-none flex flex-col items-stretch space-y-2">
        {simpleLinks.map((link) => (
          <Link key={link.href} href={link.href} className={cn(navigationMenuTriggerStyle(), navButtonStyle, "w-full px-4")}>
            {link.label}
          </Link>
        ))}
        {accordionItems.length > 0 && (
          <Accordion type="multiple" className="w-full">
            {accordionItems.map((link, index) => (
              // link.component renders an AccordionItem in vertical mode
              <link.component key={index} />
            ))}
          </Accordion>
        )}
      </div>
    );
  }

  return (
    <NavigationMenu className={menuClassName}>
      <NavigationMenuList>
        {links.map((link, index) => {
          if (link.component) return <link.component key={index} />;
          return (
            <NavigationMenuItem key={link.href} className={navButtonStyle}>
              <Link href={link.href} legacyBehavior passHref>
                <NavigationMenuLink className={cn(navigationMenuTriggerStyle(), navButtonStyle)}>
                  {link.label}
                </NavigationMenuLink>
              </Link>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
}


